import { useMemo, useState } from 'react'
import { Workflow } from 'lucide-react'

import { WorkflowExecutionStatus, type WorkflowExecution } from '@/gen/cortado/v1/cortado_pb'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { formatTimestamp } from '@/lib/date-utils'
import { useWorkflowDefinitions, useWorkflowExecutions } from '@/stores/workflows.store'
import { WorkflowExecutionDetailSheet } from '@/components/settings/workflows/workflow-execution-detail-sheet'
import { StopWorkflowRunButton } from '@/components/settings/workflows/stop-workflow-run-button'
import {
  stoppedByEventTypeLabel,
  workflowExecutionStatusLabel,
  workflowExecutionStatusVariant,
  workflowExecutionWaitingLabel,
} from '@/components/settings/workflows/workflow-execution-status'

/** Active runs (still in flight) are surfaced first and offer a Stop control. */
function isActiveStatus(status: WorkflowExecutionStatus): boolean {
  return (
    status === WorkflowExecutionStatus.RUNNING ||
    status === WorkflowExecutionStatus.PENDING
  )
}

interface EntityWorkflowRunsProps {
  /** Inquiry id (lead) or booking id (event) — the execution's source_resource_id. */
  sourceResourceId: string
  /** UPPERCASE trigger entity type, e.g. "INQUIRY" or "BOOKING". */
  triggerEntityType: string
}

/**
 * Compact per-entity workflow-run panel for lead/event detail pages. Lists the
 * entity's active runs (with a derived waiting label + Stop control) ahead of
 * recent terminal runs, and drills into the shared execution detail sheet.
 * Reuses the Stage 1 StopWorkflowRunButton and run-status helpers.
 */
export function EntityWorkflowRuns({
  sourceResourceId,
  triggerEntityType,
}: EntityWorkflowRunsProps) {
  const [selectedExecutionId, setSelectedExecutionId] = useState<string>()
  const [detailOpen, setDetailOpen] = useState(false)

  const { executions, isLoading, isError, hasLoaded } = useWorkflowExecutions({
    sourceResourceId,
    triggerEntityType,
    pageSize: 25,
    // Live-refresh the panel while it has an in-flight run; the store self-pauses
    // once every run is terminal.
    refetchActiveMs: 15000,
  })

  // Resolve workflow names from the org's definitions (cached, shared with the
  // settings list). Falls back to the definition id when a name is unavailable.
  const { workflowDefinitions } = useWorkflowDefinitions()
  const definitionNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const definition of workflowDefinitions) {
      map.set(definition.id, definition.name)
    }
    return map
  }, [workflowDefinitions])

  const { activeRuns, recentRuns } = useMemo(() => {
    const active: WorkflowExecution[] = []
    const recent: WorkflowExecution[] = []
    for (const execution of executions) {
      if (isActiveStatus(execution.status)) {
        active.push(execution)
      } else {
        recent.push(execution)
      }
    }
    return { activeRuns: active, recentRuns: recent }
  }, [executions])

  const handleOpenDetail = (executionId: string) => {
    setSelectedExecutionId(executionId)
    setDetailOpen(true)
  }

  if (isLoading && !hasLoaded) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-16 w-full rounded-md" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <Empty className="border border-dashed py-12">
        <EmptyMedia variant="icon">
          <Workflow className="h-5 w-5" />
        </EmptyMedia>
        <EmptyDescription>Workflow runs could not be loaded</EmptyDescription>
      </Empty>
    )
  }

  if (executions.length === 0) {
    return (
      <Empty className="border border-dashed py-12">
        <EmptyMedia variant="icon">
          <Workflow className="h-5 w-5" />
        </EmptyMedia>
        <EmptyDescription>No workflow runs yet</EmptyDescription>
      </Empty>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {activeRuns.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-medium uppercase text-muted-foreground">
            Active
          </h3>
          <div className="flex flex-col gap-2">
            {activeRuns.map((execution) => (
              <RunRow
                key={execution.id}
                execution={execution}
                definitionName={definitionNameById.get(execution.workflowDefinitionId)}
                onView={() => handleOpenDetail(execution.id)}
              />
            ))}
          </div>
        </section>
      )}

      {recentRuns.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-medium uppercase text-muted-foreground">
            Recent
          </h3>
          <div className="flex flex-col gap-2">
            {recentRuns.map((execution) => (
              <RunRow
                key={execution.id}
                execution={execution}
                definitionName={definitionNameById.get(execution.workflowDefinitionId)}
                onView={() => handleOpenDetail(execution.id)}
              />
            ))}
          </div>
        </section>
      )}

      <WorkflowExecutionDetailSheet
        executionId={selectedExecutionId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  )
}

interface RunRowProps {
  execution: WorkflowExecution
  definitionName?: string
  onView: () => void
}

function RunRow({ execution, definitionName, onView }: RunRowProps) {
  const waitingLabel = workflowExecutionWaitingLabel(execution)
  const stoppedByLabel =
    execution.status === WorkflowExecutionStatus.STOPPED
      ? stoppedByEventTypeLabel(execution.stoppedByEventType)
      : undefined
  const secondaryLabel =
    waitingLabel ||
    (stoppedByLabel ? `Stopped by: ${stoppedByLabel}` : undefined) ||
    execution.currentStepLabel ||
    undefined

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">
              {definitionName || `Workflow ${execution.workflowDefinitionId.slice(0, 8)}`}
            </span>
            <Badge variant={workflowExecutionStatusVariant(execution.status)}>
              {workflowExecutionStatusLabel(execution.status)}
            </Badge>
            {execution.manuallyTriggered && (
              <Badge variant="outline" className="text-muted-foreground">
                Manual
              </Badge>
            )}
          </div>
          {secondaryLabel && (
            <p className="truncate text-xs text-muted-foreground">{secondaryLabel}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {formatTimestamp(execution.matchedAt, 'MMM d, yyyy h:mm a')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isActiveStatus(execution.status) && (
            <StopWorkflowRunButton
              executionId={execution.id}
              status={execution.status}
            />
          )}
          <Button variant="outline" size="sm" onClick={onView}>
            View
          </Button>
        </div>
      </div>
    </div>
  )
}
